import React, { useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDown from "../../images/table/Arrow_down.svg";
import DownloadIcon from "@mui/icons-material/Download";
import DownloadDataButton from "./DownloadDataButton";
import { usePermission } from "../../hooks/usePermission";

// Styled components with improved responsive behavior
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    width: "100%",
    height: "35px",
    borderRadius: "4px",
    backgroundColor: "#F2F2F3",
    border: "1px solid #414958",
    padding: "0",
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 16px",
  },
  "& .MuiInputLabel-outlined": {
    transform: "translate(16px, 8px) scale(1)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75)",
    backgroundColor: "#F2F2F3",
    padding: "0 4px",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  height: "35px",
  borderRadius: "4px",
  backgroundColor: "#F2F2F3",
  border: "1px solid #414958",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "8px 16px",
  },
  [theme.breakpoints.up("md")]: {
    width: "202px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiInputLabel-outlined": {
    transform: "translate(16px, 8px) scale(1)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75)",
    backgroundColor: "#F2F2F3",
    padding: "0 4px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

export default function SearchFilter({
  filters = [],
  searchPlaceholder = "search",
  onSearchChange,
  onFilterChange,
  dataDownload = true,
  downloadRows = [],
  downloadColumns = [],
  downloadFileName = "download-data",
  downloadData,
  downloadHeaders,
  showDownload,
  onDownload,
}) {
  const ArrowDownIcon = (props) => (
    <img
      src={ArrowDown}
      alt="arrow down"
      {...props}
      style={{
        marginTop: "3px",
      }}
    />
  );

  const { t } = useTranslation();
  const { hasDownloadPermission } = usePermission();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const exportRows = downloadData || downloadRows || [];
  const exportHeaders = downloadHeaders || downloadColumns || [];
  const moduleTarget = downloadFileName || "";
  const canUserDownload = hasDownloadPermission(moduleTarget);
  const shouldShowDownload =
    dataDownload !== false &&
    showDownload !== false &&
    canUserDownload &&
    (dataDownload || showDownload || exportRows.length > 0 || exportHeaders.length > 0);

  // State for search input
  const [searchValue, setSearchValue] = useState("");

  // Initialize state for each filter with empty values
  const [filterValues, setFilterValues] = useState(
    filters.reduce((acc, filter) => {
      acc[filter.id] = "";
      return acc;
    }, {}),
  );

  // Handle search input changes
  const handleSearchChange = (event) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    if (onSearchChange) onSearchChange(newValue);
  };

  // Handle filter selection changes
  const handleFilterChange = (filterId, value) => {
    setFilterValues((prev) => {
      const newValues = { ...prev, [filterId]: value };
      if (onFilterChange) onFilterChange(newValues);
      return newValues;
    });
  };

  return (
    <Box
      className="search-filter"
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "stretch" : "center",
        width: "100%",
        gap: isMobile ? "12px" : "20px",
      }}
    >
      {/* Search TextField */}
      <Box
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : "320px",
        }}
      >
        <StyledTextField
          id="search-input"
          placeholder={t(searchPlaceholder)}
          variant="outlined"
          value={searchValue}
          onChange={handleSearchChange}
          fullWidth
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "18.75px",
            letterSpacing: "0%",
            color: "#1F242E",
            "& input::placeholder": {
              color: "#1F242E",
              opacity: 1,
            },
          }}
          InputProps={{
            endAdornment: (
              <SearchIcon sx={{ color: "#183059", margin: "0px 10px" }} />
            ),
          }}
        />
      </Box>

      {/* Filters container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "12px",
          flexWrap: isMobile ? "nowrap" : "wrap",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: isMobile ? "stretch" : "flex-end",
          width: isMobile ? "100%" : "auto",
        }}
      >
        {filters.map((filter) => (
          <StyledFormControl
            key={filter.id}
            sx={{ minWidth: isMobile ? "100%" : "202px" }}
          >
            <InputLabel
              id={`${filter.id}-label`}
              sx={{
                color: "#1F242E",
                "& input::placeholder": {
                  color: "#1F242E",
                  opacity: 1,
                },
              }}
            >
              {t(filter.label)}
            </InputLabel>
            <StyledSelect
              labelId={`${filter.id}-label`}
              id={`${filter.id}-select`}
              value={filterValues[filter.id]}
              label={filter.label}
              onChange={(e) => handleFilterChange(filter.id, e.target.value)}
              fullWidth
              IconComponent={ArrowDownIcon}
            >
              <MenuItem value="">
                <em>{t("none")}</em>
              </MenuItem>
              {filter.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>
        ))}
        {shouldShowDownload && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isMobile ? "flex-start" : "center",
              height: "35px",
            }}
          >
            <DownloadDataButton
              headers={exportHeaders}
              data={exportRows}
              onDownload={onDownload}
              filename={downloadFileName}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
